import React, { FunctionComponent, useEffect, useState } from 'react'
import { useReservationOffers } from 'components/views/reservation-offers/ReservationOffersProvider'
import { useTranslation } from 'react-i18next'
import { useMessage } from 'Elements/Message'
import { IReservationOffersListOfferData, useApi } from 'components/providers/ApiProvider'
import ListSkeleton from 'Elements/ListSkeleton'
import ListLoadingIndicator from 'Elements/ListLoadingIndicator'
import ListNoRecords from 'Elements/ListNoRecords'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import ListTablePagination from 'Elements/ListTablePagination'
import TableBody from '@material-ui/core/TableBody'
import IconButton from '@material-ui/core/IconButton'
import IconEditClients from '@material-ui/icons/Group'
import ClientsDrawer, { ClientsDrawerData } from 'components/views/reservation-offers/partials/ClientsDrawer'
import useMergeState from 'utils/mergeState'
import Tooltip from '@material-ui/core/Tooltip'
import HelpOutline from '@material-ui/icons/HelpOutline'

export type Props = {}
export type Functions = {}
export type AllProps = Props & Functions

const List: FunctionComponent<AllProps> = ({}) => {
  const api = useApi()
  const { t } = useTranslation()
  const { showError } = useMessage()

  const { listLoading, setListLoading } = useReservationOffers()

  const [offers, setOffers] = useState<{ items: IReservationOffersListOfferData[], total: number }>(null)
  const [pagination, setPagination] = useMergeState<{ perPage: number, page: number }>({
    perPage: 20,
    page: 1,
  })

  const [clientsDrawerData, setClientsDrawerData] = useState<ClientsDrawerData>(null)

  const load = (onLoad?: Function, page?: number) => {
    if (!page) {
      setListLoading(true)
    }

    api.getReservationOffersList({
      // zone_id: filter.zone || undefined,
      limit: pagination.perPage,
      page: page || pagination.page,
    })
      .then(data => {
        if (!page && data.offers && !data.offers.length && data.totalOffers) {
          const pages = Math.ceil(data.totalOffers / pagination.perPage)

          if (pagination.page > pages) {
            setPagination({ page: pages })
            load(onLoad, pages)

            return
          }
        }

        setListLoading(false)

        if (onLoad) {
          onLoad(data.offers)
        }

        setOffers({
          items: data.offers,
          total: data.totalOffers,
        })
      })
      .catch(error => {
        setListLoading(false)

        showError(`${t('alerts.couldNotLoadData')} ${t('alerts.tryLater')}`)
      })
  }

  useEffect(() => {
    load()
  }, [])

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    setPagination({ page: page + 1 })
    load()
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPagination({
      perPage: parseInt(event.target.value, 10),
      page: 1,
    })
    load()
  }

  const handleEditClients = (offer: IReservationOffersListOfferData) => {
    setClientsDrawerData({
      id: offer.id,
      modify: offer.modify && offer.modify.updatable.includes('clients'),
    })
  }

  return (
    <>
      {!offers && (
        <ListSkeleton />
      )}

      {offers && listLoading && (
        <ListLoadingIndicator />
      )}

      {offers && !offers.items.length && (
        <ListNoRecords />
      )}

      {offers && !!offers.items.length && (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('zone')}</TableCell>
                  <TableCell>{t('type')}</TableCell>
                  <TableCell>
                    {t('reservationsCount')}
                    <Tooltip title={t('reservationsCountHelp')} placement="top" arrow={true}>
                      <HelpOutline fontSize="small" style={{ verticalAlign: 'top', marginLeft: 6, color: 'rgba(0, 0, 0, 0.38)' }} />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{t('clients')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offers.items.map((offer, key) => {
                  return <TableRow key={key} hover>
                    <TableCell>
                      {offer.zone.code}
                    </TableCell>
                    <TableCell>
                      {offer.type.name}
                    </TableCell>
                    <TableCell>
                      <span style={{ color: '#ff9800' }}>{offer.active_reservations}</span>
                      {' ('}
                      <span style={{ color: 'green' }}>{offer.parked_reservations}</span>
                      {') / '}
                      <span style={{ color: 'red' }}>{Math.max(offer.max_active_reservations - offer.active_reservations, 0)}</span>
                      {' / '}
                      {offer.max_active_reservations}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="secondary"
                        size="small"
                        style={{ margin: '-5px 0' }}
                        onClick={() => handleEditClients(offer)}
                      >
                        <IconEditClients />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ListTablePagination
            count={offers.total}
            rowsPerPage={pagination.perPage}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <ClientsDrawer data={clientsDrawerData} />
        </>
      )}
    </>
  )
}

export default List
